const API_BASE_URL = process.env.REACT_APP_API_URL ?? '';
const AUTH_KEY = 'fazlic_admin_token';

function resolveImageUrl(url) {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `${API_BASE_URL}${url}`;
  return `${API_BASE_URL}/${url}`;
}

function normalizeProduct(product) {
  if (!product) return product;
  return {
    ...product,
    isAvailable: Boolean(product.isAvailable),
    featured: Boolean(product.featured),
    popupEnabled: Boolean(product.popupEnabled),
    popupOrder: product.popupOrder == null ? null : Number(product.popupOrder),
    primaryImageUrl: resolveImageUrl(product.primaryImageUrl),
    images: Array.isArray(product.images) ? product.images.map(resolveImageUrl) : []
  };
}

function mapApiError(error, fallbackMessage) {
  if (error instanceof Error && error.name === 'TypeError') {
    return new Error('Server trenutno nije dostupan. Pokušajte ponovo za nekoliko minuta.');
  }
  if (error instanceof Error) {
    return error;
  }
  return new Error(fallbackMessage);
}

function getAuthHeaders() {
  const token = localStorage.getItem(AUTH_KEY);
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

async function apiRequest(path, options = {}) {
  try {
    const { skipAuth = false, ...fetchOptions } = options;
    const response = await fetch(`${API_BASE_URL}${path}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(skipAuth ? {} : getAuthHeaders()),
        ...(fetchOptions.headers || {})
      },
      ...fetchOptions
    });

    if (!response.ok) {
      let message = `API greška (${response.status})`;
      const text = await response.text();
      if (text) {
        try {
          const errorData = JSON.parse(text);
          if (errorData?.message) message = errorData.message;
          else message = text.slice(0, 400);
        } catch {
          message = text.trim().slice(0, 400) || message;
        }
      }
      throw new Error(message);
    }

    if (response.status === 204) return null;
    const text = await response.text();
    return text ? JSON.parse(text) : null;
  } catch (error) {
    throw mapApiError(error, 'Greška pri komunikaciji sa serverom');
  }
}

export const adminService = {
  async login(username, password, rememberMe = false) {
    return await apiRequest('/api/auth/admin/login', {
      method: 'POST',
      body: JSON.stringify({ username, password, rememberMe }),
      skipAuth: true
    });
  },

  async getMe() {
    return await apiRequest('/api/auth/admin/me');
  },

  logout() {
    localStorage.removeItem(AUTH_KEY);
  },

  async getDashboard() {
    return await apiRequest('/api/admin/dashboard');
  },

  async getOrders(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.search) params.set('search', filters.search);
    const query = params.toString() ? `?${params.toString()}` : '';

    const orders = await apiRequest(`/api/admin/orders${query}`);
    return (orders || []).map(order => ({
      ...order,
      customerName: order.customerName || `${order.firstName || ''} ${order.lastName || ''}`.trim()
    }));
  },

  async getOrderById(id) {
    return await apiRequest(`/api/admin/orders/${id}`);
  },

  async rejectOrder(id) {
    return await apiRequest(`/api/admin/orders/${id}/reject`, { method: 'PATCH' });
  },

  async shipOrder(id) {
    return await apiRequest(`/api/admin/orders/${id}/ship`, { method: 'PATCH' });
  },

  async deliverOrder(id) {
    return await apiRequest(`/api/admin/orders/${id}/deliver`, { method: 'PATCH' });
  },

  async notPickedUpOrder(id) {
    return await apiRequest(`/api/admin/orders/${id}/not-picked-up`, { method: 'PATCH' });
  },

  async getCategories() {
    return await apiRequest('/api/admin/categories');
  },

  async createCategory(data) {
    return await apiRequest('/api/admin/categories', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  async updateCategory(id, data) {
    return await apiRequest(`/api/admin/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  async toggleCategory(id) {
    return await apiRequest(`/api/admin/categories/${id}/toggle-active`, {
      method: 'PATCH'
    });
  },

  async deleteCategory(id) {
    await apiRequest(`/api/admin/categories/${id}`, { method: 'DELETE' });
    return true;
  },

  async getAdminProducts() {
    const products = await apiRequest('/api/admin/products');
    return (products || []).map(normalizeProduct);
  },

  async getAdminProductById(id) {
    const product = await apiRequest(`/api/admin/products/${id}`);
    return normalizeProduct(product);
  },

  async createProduct(data) {
    const product = await apiRequest('/api/admin/products', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return normalizeProduct(product);
  },

  async updateProduct(id, data) {
    const product = await apiRequest(`/api/admin/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    return normalizeProduct(product);
  },

  async toggleAvailability(id) {
    const product = await apiRequest(`/api/admin/products/${id}/availability`, {
      method: 'PATCH'
    });
    return normalizeProduct(product);
  },

  async toggleFeatured(id) {
    const product = await apiRequest(`/api/admin/products/${id}/featured`, {
      method: 'PATCH'
    });
    return normalizeProduct(product);
  },

  async togglePopup(id) {
    const product = await apiRequest(`/api/admin/products/${id}/popup`, {
      method: 'PATCH'
    });
    return normalizeProduct(product);
  },

  async deleteProduct(id) {
    await apiRequest(`/api/admin/products/${id}`, { method: 'DELETE' });
    return true;
  },

  async getProductImages(productId) {
    const images = await apiRequest(`/api/admin/images/product/${productId}`);
    return (images || []).map(image => ({
      ...image,
      imageUrl: resolveImageUrl(image.imageUrl)
    }));
  },

  async deleteProductImage(imageId) {
    await apiRequest(`/api/admin/images/${imageId}`, { method: 'DELETE' });
    return true;
  },

  async uploadProductImages(productId, files) {
    const token = localStorage.getItem(AUTH_KEY);
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }

    const response = await fetch(`${API_BASE_URL}/api/admin/images/upload/${productId}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData
    });

    if (!response.ok) {
      let message = `API greška (${response.status})`;
      try {
        const errorData = await response.json();
        if (errorData?.message) message = errorData.message;
      } catch (e) {
        // Ignore parse errors and keep default message.
      }
      throw new Error(message);
    }

    const text = await response.text();
    if (!text) return [];
    try {
      return JSON.parse(text);
    } catch (error) {
      return [];
    }
  },

  async uploadPrimaryProductImage(productId, file) {
    const token = localStorage.getItem(AUTH_KEY);
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/api/admin/images/upload-primary/${productId}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData
    });

    if (!response.ok) {
      let message = `API greška (${response.status})`;
      try {
        const errorData = await response.json();
        if (errorData?.message) message = errorData.message;
      } catch (e) {
        // Ignore parse errors and keep default message.
      }
      throw new Error(message);
    }

    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch (error) {
      return null;
    }
  }
};
